/**
 * GA4 Event Schema Definitionen
 *
 * Definiert die erforderlichen und optionalen Parameter für alle GA4 Standard-Events.
 * Basiert auf der offiziellen GA4 Dokumentation:
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */

/**
 * Standard Item-Parameter für E-Commerce Events
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */
export const ITEM_SCHEMA = {
	// Mind. eines dieser Felder ist required
	requiredOne: ['item_id', 'item_name'],
	// Optional aber empfohlen
	recommended: [
		'affiliation',
		'coupon',
		'discount',
		'index',
		'item_brand',
		'item_category',
		'item_category2',
		'item_category3',
		'item_category4',
		'item_category5',
		'item_list_id',
		'item_list_name',
		'item_variant',
		'location_id',
		'price',
		'quantity'
	],
	// Erlaubte custom Parameter (nicht validiert)
	allowCustom: true
};

/**
 * GA4 Event Schemas
 * Struktur:
 * - required: Array von required Parametern
 * - optional: Array von optionalen Parametern
 * - requiresItems: Boolean - ob Items-Array required ist
 * - itemsSchema: Schema für Items (falls requiresItems = true)
 */
export const GA4_EVENT_SCHEMAS = {
	// E-Commerce Events
	'add_payment_info': {
		required: ['currency', 'value', 'items'],
		optional: ['coupon', 'payment_type'],
		requiresItems: true,
		itemsSchema: ITEM_SCHEMA,
		description: 'Wenn Nutzer Zahlungsinformationen hinzufügen'
	},

	'add_shipping_info': {
		required: ['currency', 'value', 'items'],
		optional: ['coupon', 'shipping_tier'],
		requiresItems: true,
		itemsSchema: ITEM_SCHEMA,
		description: 'Wenn Nutzer Versandinformationen hinzufügen'
	},

	'add_to_cart': {
		required: ['currency', 'value', 'items'],
		optional: [],
		requiresItems: true,
		itemsSchema: ITEM_SCHEMA,
		itemRequired: ['item_id', 'item_name', 'price', 'quantity'],
		description: 'Wenn Nutzer Artikel in den Warenkorb legen'
	},

	'add_to_wishlist': {
		required: ['currency', 'value', 'items'],
		optional: [],
		requiresItems: true,
		itemsSchema: ITEM_SCHEMA,
		itemRequired: ['item_id', 'item_name'],
		description: 'Wenn Nutzer Artikel zur Wunschliste hinzufügen'
	},

	'begin_checkout': {
		required: ['currency', 'value', 'items'],
		optional: ['coupon'],
		requiresItems: true,
		itemsSchema: ITEM_SCHEMA,
		description: 'Wenn Checkout-Prozess beginnt'
	},

	'purchase': {
		required: ['currency', 'transaction_id', 'value', 'items'],
		optional: ['affiliation', 'coupon', 'shipping', 'tax'],
		requiresItems: true,
		itemsSchema: ITEM_SCHEMA,
		description: 'Wenn Kauf abgeschlossen wird'
	},

	'refund': {
		required: ['currency', 'transaction_id', 'value'],
		optional: ['affiliation', 'coupon', 'shipping', 'tax', 'items'],
		requiresItems: false,
		itemsSchema: ITEM_SCHEMA,
		description: 'Wenn Rückerstattung durchgeführt wird'
	},

	'remove_from_cart': {
		required: ['currency', 'value', 'items'],
		optional: [],
		requiresItems: true,
		itemsSchema: ITEM_SCHEMA,
		itemRequired: ['item_id', 'item_name', 'price', 'quantity'],
		description: 'Wenn Nutzer Artikel aus Warenkorb entfernen'
	},

	'select_item': {
		required: ['item_list_id', 'item_list_name', 'items'],
		optional: [],
		requiresItems: true,
		itemsSchema: ITEM_SCHEMA,
		itemRequired: ['item_id', 'item_name'],
		description: 'Wenn Nutzer ein Produkt aus einer Liste auswählen'
	},

	'select_promotion': {
		required: ['creative_name', 'promotion_id', 'promotion_name'],
		optional: ['creative_slot', 'location_id', 'items'],
		requiresItems: false,
		itemsSchema: ITEM_SCHEMA,
		description: 'Wenn Nutzer eine Promotion auswählen'
	},

	'view_promotion': {
		required: ['creative_name', 'promotion_id', 'promotion_name'],
		optional: ['creative_slot', 'location_id', 'items'],
		requiresItems: false,
		itemsSchema: ITEM_SCHEMA,
		description: 'Wenn eine Promotion angezeigt wird'
	},

	'view_cart': {
		required: ['currency', 'value', 'items'],
		optional: [],
		requiresItems: true,
		itemsSchema: ITEM_SCHEMA,
		description: 'Wenn Nutzer den Warenkorb anzeigen'
	},

	'view_item': {
		required: ['currency', 'value', 'items'],
		optional: [],
		requiresItems: true,
		itemsSchema: ITEM_SCHEMA,
		itemRequired: ['item_id', 'item_name'],
		description: 'Wenn Nutzer eine Produktdetailseite anzeigen'
	},

	'view_item_list': {
		required: ['item_list_id', 'item_list_name', 'items'],
		optional: [],
		requiresItems: true,
		itemsSchema: ITEM_SCHEMA,
		itemRequired: ['item_id', 'item_name'],
		description: 'Wenn Nutzer eine Produktliste anzeigen'
	},

	// Standard Events
	'page_view': {
		required: [],
		optional: ['page_title', 'page_location', 'page_referrer'],
		requiresItems: false,
		description: 'Automatisches Seitenaufruf-Event'
	},

	'click': {
		required: [],
		optional: ['link_classes', 'link_domain', 'link_id', 'link_text', 'link_url', 'outbound'],
		requiresItems: false,
		description: 'Link-Klick Event'
	},

	'scroll': {
		required: [],
		optional: ['percent_scrolled'],
		requiresItems: false,
		description: 'Scroll-Tiefe Event'
	},

	'search': {
		required: ['search_term'],
		optional: [],
		requiresItems: false,
		description: 'Wenn Nutzer eine Suche durchführen'
	},

	'view_search_results': {
		required: ['search_term'],
		optional: ['items'],
		requiresItems: false,
		itemsSchema: ITEM_SCHEMA,
		description: 'Wenn Suchergebnisse angezeigt werden'
	},

	'login': {
		required: [],
		optional: ['method'],
		requiresItems: false,
		description: 'Wenn Nutzer sich anmelden'
	},

	'sign_up': {
		required: [],
		optional: ['method'],
		requiresItems: false,
		description: 'Wenn Nutzer sich registrieren'
	},

	'share': {
		required: [],
		optional: ['content_type', 'item_id', 'method'],
		requiresItems: false,
		description: 'Wenn Nutzer Inhalte teilen'
	}
};

/**
 * Gibt das Schema für ein spezifisches Event zurück
 * @param {string} eventName - Name des Events
 * @returns {Object|null} Event Schema oder null wenn nicht definiert
 */
export function getEventSchema(eventName) {
	return GA4_EVENT_SCHEMAS[eventName] || null;
}

/**
 * Prüft ob ein Event ein bekanntes GA4 Standard-Event ist
 * @param {string} eventName - Name des Events
 * @returns {boolean}
 */
export function isStandardEvent(eventName) {
	return eventName in GA4_EVENT_SCHEMAS;
}
